import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authAPI } from "../services/authAPI";
import { validatePassword, formatAPIError } from "../utils/validation";
import Header from "../components/layout/Header";
import "./styles/SetNewPassword.css";

const SetNewPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({ newPassword: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid password reset link. Please check your email for the correct link.");
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (error) setError("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else {
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) newErrors.newPassword = passwordError;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await authAPI.unlockNewPassword({
        token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });

      console.log("✅ Password reset response:", response.data);

      setSuccess("Password changed successfully! You can now login with your new password.");
      setFormData({ newPassword: "", confirmPassword: "" });

      setTimeout(() => {
        navigate("/login", {
          state: {
            message: "Password changed successfully. You can now login with your new password.",
            type: "success",
          },
        });
      }, 3000);
    } catch (err) {
      console.error("❌ Password reset error:", err);
      const errorMessage = formatAPIError(err);

      if (errorMessage.includes("Cannot reuse last 3 passwords")) {
        setError("Cannot reuse any of your last 3 passwords. Please choose a different password.");
      } else if (errorMessage.includes("Invalid or expired token")) {
        setError("This password reset link is invalid or expired. Please request a new link.");
      } else {
        setError(`Failed to reset password: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // No token UI state (new theme)
  if (!token) {
    return (
      <>
        <Header />
        <div className="snp-page">
          <div className="snp-card">
            <div className="snp-top">
              <div className="snp-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M7 10V8a5 5 0 0 1 10 0v2"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M7 10h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <h1 className="snp-title">Set New Password</h1>
              <p className="snp-subtitle">The password reset link is missing or invalid</p>
            </div>

            <div className="snp-body">
              {error && <div className="snp-alert snp-alert--error">{error}</div>}

              <div className="snp-footer">
                <Link className="snp-backlink" to="/login">
                  <span className="snp-backicon">←</span>
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />

      <div className="snp-page">
        <div className="snp-card">
          <div className="snp-top">
            <div className="snp-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M7 10V8a5 5 0 0 1 10 0v2"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M7 10h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h1 className="snp-title">Set New Password</h1>
            <p className="snp-subtitle">Your account has been unlocked. Please set a new password.</p>
          </div>

          <div className="snp-body">
            {error && <div className="snp-alert snp-alert--error">{error}</div>}
            {success && <div className="snp-alert snp-alert--success">{success}</div>}

            <form onSubmit={handleSubmit} className="snp-form">
              <div className="snp-field">
                <label className="snp-label">New Password</label>

                <div className="snp-inputwrap">
                  <input
                    className={errors.newPassword ? "snp-input snp-input--error" : "snp-input"}
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder="Enter new password"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="snp-eye"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    disabled={loading}
                  >
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>

                {errors.newPassword && <div className="snp-error">{errors.newPassword}</div>}
              </div>

              <div className="snp-field">
                <label className="snp-label">Confirm New Password</label>

                <div className="snp-inputwrap">
                  <input
                    className={
                      errors.confirmPassword ? "snp-input snp-input--error" : "snp-input"
                    }
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm new password"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="snp-eye"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    disabled={loading}
                  >
                    {showConfirmPassword ? "🙈" : "👁"}
                  </button>
                </div>

                {errors.confirmPassword && <div className="snp-error">{errors.confirmPassword}</div>}
              </div>

              <div className="snp-req">
                <div className="snp-req-title">Password Requirements:</div>
                <ul className="snp-req-list">
                  <li>At least 8 characters long</li>
                  <li>Contains uppercase and lowercase letters</li>
                  <li>Contains at least one number</li>
                </ul>
              </div>

              <button className="snp-btn snp-btn--primary" type="submit" disabled={loading}>
                {loading ? "Updating..." : "Set Password"}
              </button>

              <div className="snp-footer">
                <Link className="snp-backlink" to="/login">
                  <span className="snp-backicon">←</span>
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SetNewPassword;
